import { useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { AnnouncementBar } from '@/components/sections/AnnouncementBar'
import { Navbar } from '@/components/layout/Navbar'
import { useSettings } from '@/hooks/useSettings'
import { useUIStore } from '@/stores/uiStore'

const SCROLL_THRESHOLD = 80
const ANNOUNCEMENT_DISMISSED_KEY = 'ls-announcement-dismissed'

export function Header() {
  const { content_visibility } = useSettings()
  const announcementEnabled = content_visibility?.showAnnouncement !== false
  const setNavHidden = useUIStore((s) => s.setNavHidden)
  const navHidden = useUIStore((s) => s.navHidden)
  const motionReduced = useUIStore((s) => s.motionReduced)

  const [announcementDismissed, setAnnouncementDismissed] = useState(() => {
    if (typeof window === 'undefined') return false
    return sessionStorage.getItem(ANNOUNCEMENT_DISMISSED_KEY) === 'true'
  })

  const lastScrollY = useRef(0)
  const ticking = useRef(false)
  const headerRef = useRef<HTMLElement | null>(null)
  const { pathname } = useLocation()
  const isHome = pathname === '/'

  useEffect(() => {
    if (motionReduced) return

    const onScroll = () => {
      if (ticking.current) return
      ticking.current = true
      requestAnimationFrame(() => {
        const current = window.scrollY
        const directionDown = current > lastScrollY.current
        const pastThreshold = current > SCROLL_THRESHOLD

        // On the homepage the nav stays pinned through the hero and bestsellers;
        // once the collections section is reached it hands off and follows the
        // normal hide-on-scroll-down / show-on-scroll-up behaviour.
        let collectionsReached: boolean | null = null
        const headerEl = headerRef.current
        if (isHome && headerEl) {
          const collections = document.getElementById('collections')
          if (collections) {
            const collectionsTop =
              collections.getBoundingClientRect().top + current
            collectionsReached = current + headerEl.offsetHeight >= collectionsTop
          }
        }

        if (collectionsReached === false) {
          setNavHidden(false)
        } else if (pastThreshold && directionDown) {
          setNavHidden(true)
        } else if (!directionDown) {
          setNavHidden(false)
        }

        lastScrollY.current = Math.max(0, current)
        ticking.current = false
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [motionReduced, setNavHidden, isHome])

  const handleDismissAnnouncement = () => {
    setAnnouncementDismissed(true)
    try {
      sessionStorage.setItem(ANNOUNCEMENT_DISMISSED_KEY, 'true')
    } catch {}
  }

  const showAnnouncement = announcementEnabled && !announcementDismissed

  return (
    <header
      ref={headerRef}
      className="fixed inset-x-0 top-0 z-50 transition-transform duration-300 ease-out-expo"
      style={{
        // `none` (not `translateY(0)`) when visible: any transform would make the
        // header the containing block for fixed-position descendants.
        transform: navHidden ? 'translateY(-100%)' : 'none',
        paddingTop: 'max(env(safe-area-inset-top), 0px)',
      }}
    >
      <div className="bg-transparent">
        {showAnnouncement && (
          <div className="flex justify-center px-4 pt-3 sm:px-6 lg:px-10">
            <AnnouncementBar onDismiss={handleDismissAnnouncement} />
          </div>
        )}
        <div className={showAnnouncement ? 'pb-2' : ''}>
          <Navbar />
        </div>
      </div>
    </header>
  )
}
